import { useState } from 'react';
import type { StepProps } from '../OBWWizard';

// Phase 5c.5 (ADR-030 D2 Step 6) — multi-field form with jurisdiction-aware
// copy (ABN vs NZBN vs EIN hint label).

/**
 * The business-registration hint, keyed by the country given at the `residency` step
 * (ADR-124 I3). The country is no longer asked HERE: the residency step is the one place a
 * business declares it, and a second select later in the journey would overwrite that answer in
 * the flattened step data the completion step provisions from — putting the account in a zone
 * the customer never chose. Any other country gets the generic label.
 */
const REG_NUMBER_LABEL: Record<string, string> = {
  AU: 'ABN (optional)',
  NZ: 'NZBN (optional)',
  US: 'EIN (optional)',
};

// UAT punchlist F (30 Jun 2026) — the canonical SUBSCRIBER (non-partner) Cedar
// roles (admin-api/subscriber-roles.ts). Copy-forked per Structure Guide §9; do
// NOT invent roles. Default Entity Admin (the owner of a new beta entity account).
const SUBSCRIBER_ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'entity_admin', label: 'Entity Admin' },
  { value: 'group_admin', label: 'Group Admin' },
  { value: 'group_staff', label: 'Group Staff' },
];

/**
 * A deliberately permissive shape check — one `@`, a dot in the domain, no
 * spaces. The address is not verified here (the activation email is what proves
 * it); this only catches the typo that would otherwise be discovered as a failed
 * provision after the consultant has left the screen.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function StepProfile({ data, onNext, isRequired, actor, accountId }: StepProps) {
  const [businessName, setBusinessName] = useState<string>((data?.businessName as string) ?? '');
  const [businessEmail, setBusinessEmail] = useState<string>((data?.businessEmail as string) ?? '');
  const [ownerName, setOwnerName] = useState<string>((data?.ownerName as string) ?? '');
  const [role, setRole] = useState<string>((data?.role as string) ?? 'entity_admin');
  // Read-only here — answered at the `residency` step (ADR-124 I3).
  const jurisdiction = typeof data?.jurisdiction === 'string' ? (data.jurisdiction as string) : '';
  const [registrationNumber, setRegistrationNumber] = useState<string>((data?.registrationNumber as string) ?? '');
  const [websiteUrl, setWebsiteUrl] = useState<string>((data?.domainUrl as string) ?? '');
  // ADR-073 Amendment 2 (A2.4) — "Nexa Consultant" records which staff member
  // onboarded the participant. Admin-run journeys only (BOW, manual-onboard);
  // auto-filled from the logged-in staff identity, prefilled via `nexaConsultant`
  // in the seeded step data (BOW launch token carries the staff name).
  const isAdminRun = actor === 'nexa-admin';
  const [nexaConsultant, setNexaConsultant] = useState<string>((data?.nexaConsultant as string) ?? '');
  const [error, setError] = useState('');

  /**
   * ⭐ ON AN ACCOUNT-LESS JOURNEY THE BUSINESS EMAIL IS MANDATORY HERE.
   *
   * These journeys (staff SOW/BOW, the beta landing) end by PROVISIONING from
   * this form: without the email there is no Cognito user and no activation link,
   * so the completion step refuses — "subscriber email missing — Please go back
   * and complete". Ops UAT met exactly that, five screens after the field it
   * names. The requirement was always real; it was just enforced in the wrong
   * place. This mirrors the `profile` validator (validators.ts), which is the
   * durable statement of the same rule.
   *
   * `accountId` is the mount's signal and matches the validator's `accountless`:
   * an account-bearing journey already holds the owner's email (captured at the
   * public sign-up form), so there the field stays the optional business CONTACT
   * address it has always been.
   */
  const emailRequired = isRequired && !accountId;

  function commit(next: () => void) {
    setError('');
    if (isRequired && !businessName.trim()) { setError('Please enter your business name.'); return; }
    // UAT punchlist F — full name is required (it becomes the participant's
    // identity: the Cognito `name` claim + the Account ownerName).
    if (isRequired && !ownerName.trim()) { setError('Please enter the participant’s full name.'); return; }
    if (emailRequired && !businessEmail.trim()) {
      setError('Please enter the subscriber’s business email — the activation link is sent there.');
      return;
    }
    if (emailRequired && !EMAIL_SHAPE.test(businessEmail.trim())) {
      setError('Please enter a valid business email address, e.g. name@business.com.');
      return;
    }
    onNext({
      businessName: businessName.trim(),
      businessEmail: businessEmail.trim(),
      ownerName: ownerName.trim(),
      // The access role is consultant-set (admin-run journeys); self-serve
      // journeys default it server-side.
      ...(isAdminRun ? { role } : {}),
      registrationNumber: registrationNumber.trim() || undefined,
      websiteUrl: websiteUrl.trim(),
      ...(isAdminRun ? { nexaConsultant: nexaConsultant.trim() || undefined } : {}),
    });
    next();
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Business profile</h2>
      <p className="obw-subtitle">
        Tell us about your business. This information personalises trust signals across your
        dashboard and per-customer reports.
      </p>

      <p className="obw-form-section">Identity</p>
      <div className="obw-form-grid-2">
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-business-name">
            Business name {isRequired && <span aria-hidden="true">*</span>}
          </label>
          <input
            id="obw-business-name"
            className="obw-form-input"
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Acme Pty Ltd"
            required={isRequired}
          />
        </div>
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-business-email">
            Business email {emailRequired && <span aria-hidden="true">*</span>}
          </label>
          <input
            id="obw-business-email"
            className="obw-form-input"
            type="email"
            value={businessEmail}
            onChange={(e) => setBusinessEmail(e.target.value)}
            placeholder="hello@yourbusiness.com"
            autoComplete="email"
            required={emailRequired}
          />
          {emailRequired && (
            <p className="obw-form-hint">The activation link is sent to this address.</p>
          )}
        </div>
      </div>

      {/* UAT punchlist F — full name (required) + access role. */}
      <div className="obw-form-grid-2">
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-owner-name">
            Full name {isRequired && <span aria-hidden="true">*</span>}
          </label>
          <input
            id="obw-owner-name"
            className="obw-form-input"
            type="text"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Jane Smith"
            autoComplete="name"
            required={isRequired}
          />
        </div>
        {isAdminRun && (
          <div className="obw-form-group">
            <label className="obw-form-label" htmlFor="obw-role">Role</label>
            <select
              id="obw-role"
              className="obw-form-input"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {SUBSCRIBER_ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <p className="obw-form-section">Registration</p>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="obw-reg-number">
          {REG_NUMBER_LABEL[jurisdiction] ?? 'Business registration (optional)'}
        </label>
        <input
          id="obw-reg-number"
          className="obw-form-input"
          type="text"
          value={registrationNumber}
          onChange={(e) => setRegistrationNumber(e.target.value)}
          placeholder={jurisdiction === 'AU' ? '11 222 333 444' : jurisdiction === 'NZ' ? '9429 0000 0000 0' : jurisdiction === 'US' ? 'XX-XXXXXXX' : ''}
        />
      </div>

      <p className="obw-form-section">Web presence</p>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="obw-website-url">Primary website URL</label>
        <input
          id="obw-website-url"
          className="obw-form-input"
          type="url"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://yourbusiness.com"
        />
        <p className="obw-form-hint">Pre-filled from the domain you authorised. Update if your primary website differs.</p>
      </div>

      {isAdminRun && (
        <>
          <p className="obw-form-section">Nexanomics</p>
          <div className="obw-form-group">
            <label className="obw-form-label" htmlFor="obw-nexa-consultant">Nexa Consultant</label>
            <input
              id="obw-nexa-consultant"
              className="obw-form-input"
              type="text"
              value={nexaConsultant}
              onChange={(e) => setNexaConsultant(e.target.value)}
              placeholder="Consultant name"
            />
            <p className="obw-form-hint">The Nexanomics consultant onboarding this participant. Pre-filled from your staff profile.</p>
          </div>
        </>
      )}

      {error && <div className="portal-wizard-error" role="alert">{error}</div>}

      <div className="portal-wizard-nav">
        {!isRequired && (
          <button type="button" className="btn-secondary" onClick={() => commit(() => undefined)}>
            Skip for now
          </button>
        )}
        <button type="button" className="obw-btn-primary" onClick={() => commit(() => undefined)}>
          Continue
        </button>
      </div>
    </div>
  );
}
